import React from "react";
import { motion } from "motion/react";

/**
 * Loading Indicator Component
 *
 * Displays an animated loading indicator with configurable properties.
 * Can be used with no props for default styling: <LoadingIndicator />
 *
 * @example
 * // Basic usage with defaults
 * <LoadingIndicator />
 *
 * // With custom properties
 * <LoadingIndicator
 *   showText={true}
 *   dotColor={THEME_COLORS.ACCENT}
 *   animationType="wave"
 * />
 */

// Theme-based colors for light/dark mode compatibility
export const THEME_COLORS = {
  PRIMARY: "bg-gray-500 dark:bg-gray-400",
  SECONDARY: "bg-gray-400 dark:bg-gray-500",
  ACCENT: "bg-gray-600 dark:bg-gray-300",
};

interface LoadingIndicatorProps {
  className?: string;
  dotCount?: number;
  dotColor?: string;
  dotSize?: number;
  animationType?: "bounce" | "pulse" | "wave";
  showText?: boolean;
  dotSpacing?: number;
}

export function LoadingIndicator({
  className = "",
  dotCount = 3,
  dotColor = THEME_COLORS.PRIMARY,
  dotSize = 6,
  animationType = "wave",
  showText = false,
  dotSpacing = 4,
}: LoadingIndicatorProps) {
  // Dynamically calculate duration based on dot size
  // Smaller dots appear to move faster visually, so we slow them down
  // Base duration at size 6, use a gentler scaling curve
  const calculateDuration = (baseDuration: number) => {
    // Gentler scaling: size 2 = 1.5x slower, size 6 = 1x (normal)
    const sizeFactor = 1 + (6 - dotSize) * 0.125;
    return baseDuration * sizeFactor;
  };

  const getAnimationVariants = (index: number) => {
    const baseTransition = {
      repeat: Infinity,
      repeatType: "reverse" as const,
      ease: "easeInOut" as const,
    };

    // Scale animation distance based on dot size
    const bounceDistance = dotSize * 1.5; // 1.5x the dot size for bounce
    const waveDistance = dotSize; // Equal to dot size for wave

    switch (animationType) {
      case "pulse":
        return {
          animate: {
            scale: [1, 1.2, 1],
            opacity: [0.6, 1, 0.6],
            transition: {
              ...baseTransition,
              duration: calculateDuration(1.2),
              delay: index * 0.2,
            },
          },
        };
      case "bounce":
        return {
          animate: {
            y: [0, -bounceDistance, 0],
            opacity: [0.6, 1, 0.6],
            transition: {
              ...baseTransition,
              duration: calculateDuration(0.6),
              delay: index * 0.1,
            },
          },
        };
      default: // wave
        return {
          animate: {
            y: [0, -waveDistance, 0],
            opacity: [0.6, 1, 0.6],
            transition: {
              ...baseTransition,
              duration: calculateDuration(0.5),
              delay: index * 0.15,
            },
          },
        };
    }
  };

  return (
    <div
      className={`flex items-center gap-2 transition-all duration-200 ${className}`}
    >
      {showText && (
        <span className="text-gray-600 dark:text-gray-300 text-sm font-medium mr-1">
          Typing
        </span>
      )}

      <div className="flex items-center" style={{ gap: `${dotSpacing}px` }}>
        {Array.from({ length: Math.min(dotCount, 3) }).map((_, index) => (
          <motion.span
            key={index}
            className={`inline-block ${dotColor} rounded-full flex-none`}
            initial={{ opacity: 0.6 }}
            {...getAnimationVariants(index)}
            style={{
              transformOrigin: "center bottom",
              width: `${dotSize}px`,
              height: `${dotSize}px`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
